'use client';

import { Button } from '@payloadcms/ui';
import { getClassNames } from '@websolutespa/bom-core';
import React, { ReactNode, useImperativeHandle, useRef } from 'react';
import { InteractionMode, Tree, TreeEnvironmentRef, TreeInformation, TreeItem, TreeItemIndex, TreeItemRenderContext, TreeRef, UncontrolledTreeEnvironment } from 'react-complex-tree';
import './DataTree.scss';

import { DataTreeProvider, TreeItemData } from './DataTreeProvider';

const canRename = false;

export type TreeItems<T extends TreeItemData> = Record<TreeItemIndex, TreeItem<T>>;

export type DataTreeItemProps<T extends TreeItemData = TreeItemData, C extends string = never> = {
  item: TreeItem<T>;
  depth: number;
  children: React.ReactNode | null;
  title: React.ReactNode;
  arrow: React.ReactNode;
  context: TreeItemRenderContext<C>;
  info: TreeInformation;
};

export function getTreeItemClassName(props: DataTreeItemProps): string {
  const { context, item, depth, title, arrow, children } = props;
  return getClassNames(
    'rct-tree-item-title-container',
    {
      'rct-tree-item-title-container-isFolder': item.isFolder,
      'rct-tree-item-title-container-selected': context.isSelected,
      'rct-tree-item-title-container-expanded': context.isExpanded,
      'rct-tree-item-title-container-focused': context.isFocused,
      'rct-tree-item-title-container-dragging-over': context.isDraggingOver,
      'rct-tree-item-title-container-search-match': context.isSearchMatching,
    }
  );
}

export const DataTreeItemLi = (props: DataTreeItemProps & { interactive: ReactNode }): React.ReactElement => {
  const { context, item, depth, title, arrow, children, interactive } = props;
  return (
    <li className="rct-tree-item-li"
      {...(context.itemContainerWithChildrenProps)}
    >
      <div className={getTreeItemClassName(props)}
        {...(context.itemContainerWithoutChildrenProps)}
      >
        <div className="rct-tree-item-content">{interactive}</div>
      </div>
      {children}
    </li>
  );
};

export const DataTreeDragItem = (props: DataTreeItemProps): React.ReactElement => {
  const { context, item, depth, title, arrow, children } = props;
  const { extra } = item.data;
  // console.log('DataTreeDragItem', extra);
  return (
    <>
      {item.children && item.children.length > 0 && arrow}
      <button type="button" className="rct-tree-item-button"
        {...(context.interactiveElementProps as any)}
      >
        <span className="data-tree__item" style={{ paddingLeft: `${depth * 6}px` }}>
          <span className="data-tree__title">{title}</span>
          {extra && (<span className="data-tree__extra">{extra}</span>)}
        </span>
      </button>
      {canRename && (
        <Button type="button" onClick={context.startRenamingItem}>
          Rename
        </Button>
      )}
      <Button type="button" buttonStyle="pill" size="small" icon={(
        <svg clipRule="evenodd" fillRule="evenodd" strokeLinejoin="round" strokeMiterlimit="2" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
          <path fill="currentColor" d="m19 20.25c0-.402-.356-.75-.75-.75-2.561 0-11.939 0-14.5 0-.394 0-.75.348-.75.75s.356.75.75.75h14.5c.394 0 .75-.348.75-.75zm-7.403-3.398 9.124-9.125c.171-.171.279-.423.279-.684 0-.229-.083-.466-.28-.662l-3.115-3.104c-.185-.185-.429-.277-.672-.277s-.486.092-.672.277l-9.143 9.103c-.569 1.763-1.555 4.823-1.626 5.081-.02.075-.029.15-.029.224 0 .461.349.848.765.848.511 0 .991-.189 5.369-1.681zm-3.27-3.342 2.137 2.137-3.168 1.046zm.955-1.166 7.651-7.616 2.335 2.327-7.637 7.638z" fillRule="nonzero" />
        </svg>
      )} iconPosition="left" onClick={context.primaryAction}>
        Edit
      </Button>
    </>
  );
};

export const DataTreeRenameItem = (props: DataTreeItemProps): React.ReactElement => {
  const { context, item, depth, title, arrow, children } = props;
  const { extra } = item.data;
  console.log('DataTreeRenameItem', extra);
  return (
    <>
      {item.children && item.children.length > 0 && arrow}
      <div className="rct-tree-item-button" {...(context.interactiveElementProps)}>
        <span className="data-tree__item" style={{ paddingLeft: `${depth * 6}px` }}>
          <span className="data-tree__title">{title}</span>
          {extra && (<span className="data-tree__extra">{extra}</span>)}
        </span>
      </div>
    </>
  );
};

export const DataTreeItem = (props: DataTreeItemProps): React.ReactElement => {
  const { context, item, depth, title, arrow, children } = props;
  return (
    <DataTreeItemLi {...props} interactive={
      context.isRenaming ?
        <DataTreeRenameItem {...props} /> :
        <DataTreeDragItem {...props} />
    } />
  );
};

export type DataTreeInstance<T extends TreeItemData> = {
  onSetData: (item: TreeItem<T>, data: T) => TreeItem<T>;
};

export type DataTreeProps<T extends TreeItemData> = {
  items: TreeItems<T>;
  rootItem?: string;
  treeId?: string;
  treeLabel?: string;
  onChange?: (items: TreeItems<T>) => void;
  onEdit?: (items: TreeItem<T>, treeId: string) => void;
};

function DataTree_<T extends TreeItemData>(
  props: DataTreeProps<T>,
  ref: React.ForwardedRef<DataTreeInstance<T>>
) {

  const { items, rootItem = '_root', treeId = 'tree', treeLabel = 'Label', onChange, onEdit } = props;

  const treeEnvironment = useRef<TreeEnvironmentRef>(null);
  const tree = useRef<TreeRef>(null);

  const expandedItem = items[rootItem]?.children ? items[rootItem]?.children[0] : undefined;

  const viewState = {
    tree: {
      expandedItems: expandedItem ? [expandedItem] : [],
    },
  };

  const onSetData = (item: TreeItem<T>, data: T) => ({
    ...item,
    data,
  });

  useImperativeHandle(ref, () => ({
    onSetData,
  }));

  const dataProvider = new DataTreeProvider(items, onSetData);

  dataProvider.onItemsDidChange((items) => {
    // console.log('onItemsDidChange', items);
    if (typeof onChange === 'function') {
      onChange(items);
    }
  });

  /*
  dataProvider.onDidChangeTreeData(async (changedItemIds: TreeItemIndex[]) => {
    const item = await dataProvider.getTreeItem(changedItemIds[0]);
    console.log('onDidChangeTreeData', changedItemIds, item);
  });
  */

  const getItemTitle = (item: TreeItem<T>) => item.data.title;

  /*
  const onStateDidChange = () => {
    const currentItems = treeEnvironment.current.items;
    console.log(
      'onStateDidChange',
      'items', Object.keys(items).join(', '),
      'currentItems', Object.keys(currentItems).join(', '),
      items === currentItems,
      JSON.stringify(items) === JSON.stringify(currentItems),
    );
  };

  const onDrop = (items: TreeItem<any>[], target: DraggingPosition) => {
    console.log('onDrop', items, target);
    console.log(tree.current, treeEnvironment.current);
    console.log(treeEnvironment.current.items['product_index'].children.join(', '));
    onStateDidChange();
  };

  const onRenameItem = (item: TreeItem<any>, name: string, treeId: string) => {
    console.log('onRenameItem', item, treeId);
    onStateDidChange();
  };

  */

  const onPrimaryAction = (items: TreeItem<T>, treeId: string) => {
    // console.log('onPrimaryAction', items, treeId);
    if (typeof onEdit === 'function') {
      onEdit(items, treeId);
    }
  };

  const onExpandAll = (event: React.MouseEvent) => {
    tree.current?.expandAll();
  };

  const onCollapseAll = (event: React.MouseEvent) => {
    tree.current?.collapseAll();
  };

  /*
  useEffect(() => {
    tree.current?.collapseAll();
    tree.current?.expandAll();
    console.log('useEffect.expandAll');
  }, [items]);
  */

  const baseClass = 'category-tree';

  return (
    <>
      <div className="list-controls">
        <div className="list-controls__wrap">
          <div className={`${baseClass}__actions`}>
            <button type="button" onClick={onExpandAll}>
              Expand
            </button>
            <button type="button" onClick={onCollapseAll}>
              Collapse
            </button>
          </div>
        </div>
      </div>
      <UncontrolledTreeEnvironment<T>
        ref={treeEnvironment}
        dataProvider={dataProvider}
        getItemTitle={getItemTitle}
        viewState={viewState}
        /*
        renderTreeContainer={({ children, containerProps }) => (
          <div {...containerProps}>{children}</div>
        )}
        renderItemsContainer={({ children, containerProps }) => (
          <ul {...containerProps}>{children}</ul>
        )}
        */
        renderItem={DataTreeItem}
        /*
        renderItemTitle={({ title }) => <span>{title}</span>}
        renderItemArrow={({ item, context }) =>
          item.isFolder ? (
            context.isExpanded ? (
              <span>{'>'}</span>
            ) : (
              <span>v</span>
            )
          ) : null
        }
        */
        defaultInteractionMode={InteractionMode.ClickArrowToExpand}
        canDragAndDrop={true}
        canReorderItems={true}
        canDropOnFolder={true}
        canDropOnNonFolder={false}
        canRename={canRename}
        /*
        onDrop={onDrop}
        onRenameItem={onRenameItem}
        */
        onPrimaryAction={onPrimaryAction}
      >
        <Tree ref={tree} rootItem={rootItem} treeId={treeId} treeLabel={treeLabel} />
      </UncontrolledTreeEnvironment>
    </>
  );
}

export const DataTree = React.forwardRef(DataTree_) as <T extends TreeItemData>(
  props: DataTreeProps<T> & { ref?: React.ForwardedRef<DataTreeInstance<T>> }
) => ReturnType<typeof DataTree_>;
